import React, { useState, useRef, useEffect } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import SearchIcon from '../Icons/SearchIcon';
import GlobalErrorListCard from '../../molecules/GlobalErrorListCard';

interface Option {
  id: string;
  name: string;
  transmission?: string;
  transmissionValue?: string;
}

interface CascaderProps {
  options: Option[];
  onSelect: (option: Option) => void;
  placeholder?: string;
  placeholderInputSelect?: string;
  showIconSearch?: boolean;
  optionLabel?: string;
  label?: string;
  value: string | number | null;
  inputClassName?: string;
  labelClassName?: string;
  required?: boolean;
  isDisable?: boolean;
  isTypeCar?: boolean;
  isTypeYear?: boolean;
  isError?: boolean;
  isRefetch?: boolean;
  refetch?: () => void;
  insertAlertBox?: React.ReactNode;
}

const CascaderDropdown = ({
  options,
  onSelect,
  placeholder = 'Pilih Model Mobil',
  placeholderInputSelect = 'Cari...',
  showIconSearch = false,
  optionLabel = '',
  label,
  value,
  inputClassName = '',
  labelClassName = '',
  required = false,
  isDisable = false,
  isTypeCar,
  isTypeYear,
  isError,
  isRefetch,
  refetch,
  insertAlertBox,
}: CascaderProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [filteredOptions, setFilteredOptions] = useState<any[]>([]);
  const [section, setSection] = useState('A/T');

  const listSection = [
    {
      key: 'A/T',
      label: 'OTOMATIS',
    },
    {
      key: 'M/T',
      label: 'MANUAL',
    },
  ].filter((item) => {
    return options?.some((opt) => opt.transmission === item.key);
  });

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Set Section if 1 Section empty
  useEffect(() => {
    const getListCarAT = options?.filter((item) => item.transmission === 'A/T');
    const getListCarMT = options?.filter((item) => item.transmission === 'M/T');
    const optionValue = options?.find((item) => item.id === value);
    if (optionValue) {
      setSection(optionValue.transmission);
    } else {
      if (getListCarAT.length === 0) {
        setSection('M/T');
      }
      if (getListCarMT.length === 0) {
        setSection('A/T');
      }
    }
  }, [options, value]);

  // Handle closing dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search input

  const handleOptionClick = (option: Option) => {
    setSelectedOption(option); // Set selected value
    onSelect(option); // Trigger callback
    setIsDropdownOpen(false);
  };

  const valueLabel = options?.find((item) => item.id === value);
  const displayNameDropDown = () => {
    if (valueLabel) {
      return valueLabel.name ? valueLabel.name : placeholder;
    }
    return value ? value : placeholder;
  };

  useEffect(() => {
    const key = optionLabel ? optionLabel : 'name';
    const filterOpt = options?.filter((option) => {
      if (!isTypeCar) {
        return searchTerm?.length >= 3
          ? option[key]?.toLowerCase().includes(searchTerm.toLowerCase())
          : true;
      } else {
        return option.transmission === section;
      }
    });
    setFilteredOptions(filterOpt);
  }, [section, searchTerm, options]);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Label */}
      {label && (
        <label className={`block text-sm font-medium text-gray-700 mb-1.5 ${labelClassName}`}>
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}

      {/* Main Input */}
      <div
        className={`w-full outline-none px-4 py-2 placeholder:text-reliableBlack60 placeholder:line-clamp-1 border-b border-reliableBlack30 focus:border-reliableBlack60 lg:px-5 lg:py-4 ${isDisable ? 'cursor-not-allowed bg-reliableBlack10 text-reliableBlack50' : 'cursor-pointer bg-reliableBlack3 text-reliableBlack'} flex justify-between max-h-[3.25rem] ${inputClassName}`}
        onClick={() => {
          if (!isDisable) setIsDropdownOpen((prev) => !prev);
        }}
      >
        <span className="line-clamp-1">{displayNameDropDown()}</span>
        <span className="text-gray-500">
          {isDropdownOpen ? <FiChevronUp /> : <FiChevronDown />}
        </span>
      </div>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div
          className={`absolute z-10 w-full bg-white border border-gray-300 mt-1 rounded-md shadow-md ${isError ? '' : 'max-h-80'} overflow-y-auto`}
        >
          {isError ? (
            <GlobalErrorListCard isRefetch={isRefetch} refetch={refetch} />
          ) : (
            <>
              {/* Search Input */}
              <div className="p-2">
                {isTypeCar ? (
                  <div className="flex w-full h-[62px] px-4 py-2">
                    <div className="w-full h-full flex p-1">
                      {listSection.map(
                        (
                          res: {
                            key: string;
                            label: string;
                          },
                          idx: number
                        ) => {
                          return (
                            <div
                              key={idx}
                              onClick={() => {
                                setSection(res.key);
                              }}
                              className={`flex ${listSection.length === 2 ? 'w-1/2' : 'w-full'} cursor-pointer font-semibold text-xs text-[background: #FFFEFE] text-center justify-center items-center rounded ${res.key === section ? 'text-white bg-[#231F20]' : 'text-[231F20] bg-[#F4F4F4]'}`}
                            >
                              {res.label}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                ) : isTypeYear ? null : (
                  <div className="flex relative">
                    {showIconSearch && (
                      <div className="absolute top-1/2 -translate-y-1/2 left-3">
                        <SearchIcon size={20} fill="#777474" />
                      </div>
                    )}
                    {insertAlertBox ?? (
                      <input
                        type="text"
                        className={`w-full px-4 py-2 border border-gray-200 rounded-md outline-none ${showIconSearch ? 'pl-10' : ''}`}
                        placeholder={placeholderInputSelect}
                        value={searchTerm}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSearchTerm(value);
                        }}
                        autoFocus
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Options */}
              <ul className="py-1 max-h-32 overflow-y-auto">
                {filteredOptions?.length > 0 ? (
                  filteredOptions.map((option) => (
                    <li
                      key={option.id}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleOptionClick(option)}
                    >
                      {option.name || option[optionLabel]}
                    </li>
                  ))
                ) : (
                  <li className="px-4 py-2 text-gray-500">Tidak ada pilihan</li>
                )}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CascaderDropdown;
